import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { clearVehicle, fetchVehicle } from "../../../store/actions/vehicle";
import { logo } from "../../../images";
import LanguageDropdown from "../../elements/language";
import "./login.css";

// Aktenzeichen muss vollständig mit allen Bindestrichen eingegeben werden, z. B. K10-620-260-520-074-556
const isValidFileNo = (value) => /^[KS]?\d{1,3}(-\d{3}){3,}$/.test(value.trim());

// Baut das Kennzeichen im bisherigen API-Format "ORTSKENNUNG-REST" (z. B. "LÖ-AB123").
// DE: Ortskennung muss mit Bindestrich oder Leerzeichen abgetrennt sein.
const buildPlateCode = (value, country) => {
  const input = value.trim().toUpperCase();
  if (country === "de") {
    const match = input.match(/^([A-ZÄÖÜ]{1,3})[\s-]+([A-ZÄÖÜ0-9][A-ZÄÖÜ0-9\s-]*)$/);
    if (!match) return null;
    const rest = match[2].replace(/[\s-]/g, "");
    if (rest.length < 2 || rest.length > 7) return null;
    return `${match[1]}-${rest}`;
  }
  const foreign = input.replace(/[\s-]/g, "");
  return /^[A-Z0-9]{5,12}$/.test(foreign) ? foreign : null;
};

const HelpIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M9.5 9.5a2.5 2.5 0 015 .3c0 1.6-2.5 1.9-2.5 3.4M12 16.5h.01" />
  </svg>
);

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const vehicle = useSelector((state) => state.vehicle);

  const [fileNo, setFileNo] = useState("");
  const [plate, setPlate] = useState("");
  const [country, setCountry] = useState("de");
  const [errors, setErrors] = useState({ fileNo: false, plate: false });
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    document.title = "Ticket Portal - innopark24";
  }, []);

  // Nach erfolgreicher Abfrage zur Fallansicht, bei Fehler Meldung anzeigen
  useEffect(() => {
    if (vehicle.data.parkingProgress) {
      navigate("/start");
    }
    if (vehicle.error !== null) {
      setSubmitError(t("lgNotFound"));
      dispatch(clearVehicle());
    }
  }, [vehicle, navigate, dispatch, t]);

  const onFileNoChange = (event) => {
    setFileNo(event.target.value.toUpperCase().replace(/[^0-9KS-]/g, ""));
    setErrors((e) => ({ ...e, fileNo: false }));
    setSubmitError("");
  };

  const onPlateChange = (event) => {
    setPlate(event.target.value.toUpperCase().replace(/[^A-ZÄÖÜ0-9\s-]/g, ""));
    setErrors((e) => ({ ...e, plate: false }));
    setSubmitError("");
  };

  const onSubmit = (event) => {
    event.preventDefault();
    setSubmitError("");
    const code = buildPlateCode(plate, country);
    const nextErrors = { fileNo: !isValidFileNo(fileNo), plate: !code };
    setErrors(nextErrors);
    if (nextErrors.fileNo || nextErrors.plate) return;
    dispatch(fetchVehicle({ fileNo: fileNo.trim(), code }));
  };

  const isGerman = country === "de";

  return (
    <div className="ipl-page">
      <div className="ipl-left">
        <header className="ipl-header">
          <a href="https://innopark24.com/" aria-label="innopark24">
            <img className="ipl-logo" src={logo} alt="innopark24" />
          </a>
          <LanguageDropdown label={t("lgLanguage")} />
        </header>

        <div className="ipl-form-wrap">
          <section className="ipl-card">
            <h1>{t("lgTitle")}</h1>
            <p className="ipl-lead">
              <strong>{t("lgLeadHead")}</strong>
              <span>{t("lgLead")}</span>
            </p>

            {submitError && (
              <div className="ipl-status" role="alert">
                {submitError}
              </div>
            )}

            <form onSubmit={onSubmit} noValidate>
              <div className={`ipl-field ${errors.fileNo ? "has-error" : ""}`}>
                <label htmlFor="ipl-fileNo">{t("lgFileNo")}</label>
                <div className="ipl-input">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9z" />
                    <path d="M14 3v6h6" />
                  </svg>
                  <input id="ipl-fileNo" type="text" autoComplete="off" spellCheck="false" placeholder="K10-620-260-520-074-556" value={fileNo} onChange={onFileNoChange} />
                  <span className="ipl-help" tabIndex="0" aria-label={t("lgFileNoTip")}>
                    <HelpIcon />
                    <span className="ipl-tip" role="tooltip">{t("lgFileNoTip")}</span>
                  </span>
                </div>
                <div className="ipl-hint">{t("lgFileNoHint")}</div>
                <div className="ipl-err">{t("lgFileNoError")}</div>
              </div>

              <div className={`ipl-field ${errors.plate ? "has-error" : ""}`}>
                <label htmlFor="ipl-plate">{t("lgPlate")}</label>
                <div className="ipl-plate-row">
                  <div className="ipl-country">
                    <select
                      aria-label={t("lgCountry")}
                      value={country}
                      onChange={(e) => {
                        setCountry(e.target.value);
                        setErrors((x) => ({ ...x, plate: false }));
                      }}
                    >
                      <option value="de">DE</option>
                      <option value="int">Int</option>
                    </select>
                  </div>
                  <div className="ipl-input">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 17h14v-4l-2-6H7l-2 6z" />
                      <path d="M5 13h14M6 17v2M18 17v2" />
                    </svg>
                    <input id="ipl-plate" type="text" autoComplete="off" autoCapitalize="characters" spellCheck="false" maxLength={14} placeholder={isGerman ? "M-AB1234" : "AB123CD"} value={plate} onChange={onPlateChange} />
                    <span className="ipl-help" tabIndex="0" aria-label={t(isGerman ? "lgPlateTipDe" : "lgPlateTipInt")}>
                      <HelpIcon />
                      <span className="ipl-tip" role="tooltip">{t(isGerman ? "lgPlateTipDe" : "lgPlateTipInt")}</span>
                    </span>
                  </div>
                </div>
                <div className="ipl-hint">{t(isGerman ? "lgPlateHintDe" : "lgPlateHintInt")}</div>
                <div className="ipl-err">{t(isGerman ? "lgPlateErrorDe" : "lgPlateErrorInt")}</div>
              </div>

              <button className={`ipl-btn ${vehicle.isLoading ? "loading" : ""}`} type="submit" disabled={vehicle.isLoading}>
                <span className="ipl-spinner" aria-hidden="true"></span>
                <span>{vehicle.isLoading ? t("lgSearching") : t("search")}</span>
              </button>
            </form>

            <div className="ipl-divider"></div>

            <div className="ipl-help-sec">
              <h2>{t("lgHelpHead")}</h2>
              <p>{t("lgHelpText")}</p>
              <div className="ipl-help-btns">
                <Link className="ipl-pill soft" to="/faq">
                  {t("lgFaqButton")}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 6l6 6-6 6" />
                  </svg>
                </Link>
                <a className="ipl-pill line" href="https://innopark24.com/autofahrer/">
                  {t("lgContactButton")}
                </a>
              </div>
            </div>
          </section>
        </div>

        <footer className="ipl-footer">
          <Link to="/imprint">{t("lgImprint")}</Link>
          <Link to="/privacy">{t("lgPrivacy")}</Link>
          <Link to="/faq">FAQ</Link>
        </footer>
      </div>

      <aside className="ipl-right">
        <div className="ipl-info">
          <span className="ipl-eyebrow">{t("lgInfoEyebrow")}</span>
          <h2>{t("lgInfoHead")}</h2>
          <p className="ipl-info-lead">{t("lgInfoLead")}</p>
          <ul className="ipl-steps">
            {[1, 2, 3].map((n) => (
              <li key={n}>
                <span className="ipl-num">{n}</span>
                <div>
                  <strong>{t(`lgStep${n}Head`)}</strong>
                  <span>{t(`lgStep${n}Text`)}</span>
                </div>
              </li>
            ))}
          </ul>
          <div className="ipl-note">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8v5M12 16h.01" />
            </svg>
            <p>
              <strong>{t("lgNoteHead")}</strong>
              <span>{t("lgNoteText")}</span>
            </p>
          </div>
        </div>
      </aside>
    </div>
  );
};

export default Login;
